﻿import { useEffect, useRef, useState, useCallback } from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useRotatingWelcomeCopy } from '@/hooks/useRotatingWelcomeCopy'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { FilePopup } from './AgentChatMessage'
import { AgentNarrativeMessage } from './AgentNarrativeMessage'
import AgentSidePanel from '../agent/AgentSidePanel'
import { StudioPanel } from '../studio/StudioPanel'
import '../studio/studio-panel.css'
import { useApp } from '@/store/AppContext'
import { useChat } from '@/hooks/useChat'
import { useAgent, type AgentMode } from '@/hooks/useAgent'
import { useUserMemory } from '@/hooks/useUserMemory'
import { useAuth } from '@/hooks/useAuth'
import { useNav } from '@/App'
import type { AgentFile, Message, MessageAttachment } from '@/types'
import {
  Globe, Code2, FileText, BarChart3, Zap, Rocket, Layers, Webhook, AppWindow, Cpu
} from 'lucide-react'
import { HyperspeedCanvas } from './HyperspeedCanvas'
import { type AgentSettings } from './AgentSettingsPopover'
import { useCredits } from '@/hooks/useCredits'
import { useAgentConcurrency, registerAgentRun, unregisterAgentRun } from '@/hooks/useAgentConcurrency'
import { useConversationRuntime } from '@/hooks/useConversationRuntime'
import { getConversationRuntimeSurface } from '@/lib/workspace'
import { getPreferredComputerMode, type ComputerPanelMode } from '@/lib/computerPanelModel'
import {
  fetchActiveAgentRun,
  fetchAgentRunState,
  normalizeRunStateComputerFrames,
  normalizeRunStateEvents,
  normalizeRunStateFiles,
  normalizeRunStatePlanningTasks,
  selectHydratedAgentContent,
} from '@/services/agentRunState'

type ComputerPanelTab = ComputerPanelMode
type SidePanelContext = {
  conversationId: string
  runId: string | null
}
const LIVE_AGENT_STATUSES = ['connecting', 'planning', 'running', 'reconnecting', 'recovering', 'waiting_approval', 'needs_takeover', 'takeover_active'] as const

function isLiveAgentStatus(status: string): boolean {
  return LIVE_AGENT_STATUSES.includes(status as typeof LIVE_AGENT_STATUSES[number])
}

function findLatestAgentRunMessage(messages: Message[] = []): Message | undefined {
  return [...messages]
    .reverse()
    .find(message => (message.role === 'agent' || message.isAgentMessage) && Boolean(message.agentRunId))
}

// â”€â”€â”€ Velkomst-forslag â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const _CHAT_SUGGESTIONS = [
  { icon: '💬', label: 'Forklar et konsept' },
  { icon: '⌨️', label: 'Skriv kode' },
  { icon: '📝', label: 'Lag et sammendrag' },
  { icon: '🔍', label: 'Analyser tekst' },
  { icon: '🌐', label: 'Oversett' },
]

const _AGENT_SUGGESTIONS = [
  { icon: <Globe size={13} />, label: 'Søk og analyser nett' },
  { icon: <Code2 size={13} />, label: 'Bygg en app' },
  { icon: <BarChart3 size={13} />, label: 'Analyser data' },
  { icon: <FileText size={13} />, label: 'Skriv rapport' },
  { icon: <Zap size={13} />, label: 'Automatiser oppgave' },
  { icon: <Code2 size={13} />, label: 'Kjør skript' },
]

const _WRITING_SUGGESTIONS = [
  { icon: '📄', label: 'Skriv en rapport' },
  { icon: '📚', label: 'Skriv et kapittel' },
  { icon: '🎓', label: 'Hjelp med eksamen' },
  { icon: '✍️', label: 'Skriv en artikkel' },
  { icon: '📖', label: 'Start en bok' },
]

const STUDIO_SUGGESTIONS = [
  { icon: <AppWindow size={13} />, label: 'Bygg et grensesnitt' },
  { icon: <Webhook size={13} />, label: 'Sett opp en trigger' },
  { icon: <Layers size={13} />, label: 'Lag en multi-agent flyt' },
  { icon: <Cpu size={13} />, label: 'Automatiser en prosess' },
  { icon: <Code2 size={13} />, label: 'Bygg en app' },
]

// (Aurora-bakgrunn fjernet fra chat)

// â”€â”€â”€ Velkomst-layout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function WelcomeLayout({
  title,
  subtitle,
  animatedPlaceholderText,
  animationKey,
  isAgent,
  isStudio,
  chatInputProps,
  blobFadingOut: _blobFadingOut,
  showHyperspeed,
  onHyperspeedComplete,
}: {
  title: string
  subtitle?: string
  animatedPlaceholderText?: string
  animationKey: string
  isAgent: boolean
  isStudio?: boolean
  chatInputProps: Parameters<typeof ChatInput>[0]
  blobFadingOut: boolean
  showHyperspeed: boolean
  onHyperspeedComplete: () => void
}) {
  const { isDark } = useTheme()
  const titleColor = isDark ? '#E5E5E5' : '#1A1A1A'
  const subtitleColor = isDark ? '#6B7280' : '#6A6A6A'

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 24px 24px',
      minHeight: 0,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Studio gradient blobs â€” multiagent/studio palette */}
      {isStudio && (
        <>
          {/* Deep violet â€” left top */}
          <div style={{ position: 'absolute', width: 480, height: 480, top: -160, left: -100, borderRadius: '50%', filter: 'blur(90px)', background: 'radial-gradient(circle, transparent 0, rgba(109,40,217,0.28) 70%)', pointerEvents: 'none', zIndex: 0, animation: 'blob-fade-in 1.2s ease forwards', animationDelay: '0.1s', opacity: 0 }} />
          {/* Cyan/teal â€” right top */}
          <div style={{ position: 'absolute', width: 420, height: 420, top: -100, right: -100, borderRadius: '50%', filter: 'blur(90px)', background: 'radial-gradient(circle, transparent 0, rgba(6,182,212,0.20) 70%)', pointerEvents: 'none', zIndex: 0, animation: 'blob-fade-in 1.2s ease forwards', animationDelay: '0.3s', opacity: 0 }} />
          {/* Magenta/pink â€” bottom center */}
          <div style={{ position: 'absolute', width: 360, height: 360, bottom: -120, left: '30%', borderRadius: '50%', filter: 'blur(80px)', background: 'radial-gradient(circle, transparent 0, rgba(217,70,239,0.14) 70%)', pointerEvents: 'none', zIndex: 0, animation: 'blob-fade-in 1.2s ease forwards', animationDelay: '0.5s', opacity: 0 }} />
        </>
      )}

      {/* Hyperspeed canvas (agent-modus) */}
      {isAgent && !isStudio && (
        <HyperspeedCanvas active={showHyperspeed} onFadeComplete={onHyperspeedComplete} />
      )}

      {/* Tittel */}
      <h1
        key={`title-${animationKey}`}
        className="welcome-hero-title welcome-hero-title--animated"
        style={{
        position: 'relative',
        zIndex: 2,
        fontSize: 'clamp(24px, 4vw, 36px)',
        fontWeight: 300,
        color: titleColor,
        textAlign: 'center',
        marginBottom: subtitle ? 8 : 28,
        letterSpacing: '-0.02em',
        fontFamily: '"Georgia", serif',
      }}
      >
        {title}
      </h1>

      {subtitle && (
        <p
          key={`subtitle-${animationKey}`}
          className="welcome-hero-subtitle welcome-hero-subtitle--animated"
          style={{
          position: 'relative',
          zIndex: 2,
          fontSize: 14,
          color: subtitleColor,
          textAlign: 'center',
          marginBottom: 28,
        }}
        >
          {subtitle}
        </p>
      )}

      {/* Chat-input â€” show SuggestionPills (not compact) so the new dropdown pills appear */}
      <div style={{ width: '100%', maxWidth: 680, position: 'relative', zIndex: 2 }}>
        <ChatInput
          {...chatInputProps}
          isWelcome
          animatedPlaceholderText={animatedPlaceholderText}
          welcomeAnimationKey={animationKey}
        />
      </div>
    </div>
  )
}

// â”€â”€â”€ Hoved ChatView â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export function ChatView() {
  const { activeConversation, settings, updateSettings, updateAgentMessage, updateConversationMeta } = useApp()
  const { sendMessage, stopStreaming, isStreaming, handleAskUserSubmit } = useChat()
  const { state: agentState, startAgent, resumeAgentRun, stopAgent, approveAction, fetchFileContent, requestBrowserTakeover, resumeBrowserFromTakeover } = useAgent()
  const { user } = useAuth()
  const { profile } = useCredits(user?.id ?? null)
  const plan = (profile?.plan ?? 'free') as 'free' | 'pro'
  const credits = profile?.credits ?? null
  const hasNoCredits = credits !== null && credits <= 0
  const { canStartAgent } = useAgentConcurrency(plan)
  const [showUpgradeCTA, setShowUpgradeCTA] = useState(false)
  // Credits banner state: 'empty' = out of credits, 'refilled' = credits added, null = hidden
  const [creditsBannerState, setCreditsBannerState] = useState<'empty' | 'refilled' | null>(null)
  const prevCreditsRef = useRef<number | null>(null)
  const rotatingChatWelcome = useRotatingWelcomeCopy('chat', settings.language)
  const rotatingAgentWelcome = useRotatingWelcomeCopy('agent', settings.language)
  const { openSettingsTab } = useApp()
  const { memory, addMemory, removeMemory, clearMemory, extractFromMessage } = useUserMemory(user?.id)

  // Sync memory to settings so useChat can access it without prop drilling
  useEffect(() => {
    updateSettings({ userMemory: memory })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memory])

  // Monitor credits: show empty banner when credits hit 0, show refilled banner when credits go from 0 to >0
  useEffect(() => {
    if (credits === null) return
    const prev = prevCreditsRef.current
    if (prev !== null && prev <= 0 && credits > 0) {
      // Credits were refilled
      setCreditsBannerState('refilled')
    } else if (credits <= 0 && (prev === null || prev > 0)) {
      // Just ran out of credits
      setCreditsBannerState('empty')
    }
    prevCreditsRef.current = credits
  }, [credits])

  const [agentMode, setAgentMode] = useState<AgentMode>('safe')
  const [showSidePanel, setShowSidePanel] = useState(false)
  const [sidePanelPreferredTab, setSidePanelPreferredTab] = useState<ComputerPanelTab | undefined>(undefined)
  const [sidePanelContext, setSidePanelContext] = useState<SidePanelContext | null>(null)
  // useAgentMode is derived from activeConversation.useAgentMode, with local override
  const [useAgentMode, setUseAgentModeLocal] = useState(false)
  const {
    pendingAgentTask,
    setPendingAgentTask,
    useAgentMode: navUseAgentMode,
    setUseAgentMode: setNavUseAgentMode,
    appMode,
    setAppMode,
    studioOpen,
    setStudioOpen,
  } = useNav()

  // Sync from header toggle (NavContext) to local state
  const prevNavAgentModeRef = useRef(navUseAgentMode)
  useEffect(() => {
    if (navUseAgentMode !== prevNavAgentModeRef.current) {
      prevNavAgentModeRef.current = navUseAgentMode
      setUseAgentModeLocal(navUseAgentMode)
      if (activeConversation?.id) {
        updateConversationMeta(activeConversation.id, { useAgentMode: navUseAgentMode })
      }
    }
  }, [navUseAgentMode, activeConversation?.id, updateConversationMeta])

  // Sync useAgentMode from active conversation when switching chats
  const prevConvIdRef = useRef<string | null>(null)
  useEffect(() => {
    const convId = activeConversation?.id ?? null
    if (convId !== prevConvIdRef.current) {
      prevConvIdRef.current = convId
      const val = activeConversation?.useAgentMode ?? false
      setUseAgentModeLocal(val)
      setNavUseAgentMode(val)
      setShowSidePanel(false)
      setSidePanelPreferredTab(undefined)
      setSidePanelContext(null)
      setOpenFile(null)
      setOpenFileAllFiles([])
    }
  }, [activeConversation?.id, activeConversation?.useAgentMode, setNavUseAgentMode])

  // Persist useAgentMode changes back to conversation and sync to NavContext (header)
  const setUseAgentMode = useCallback((val: boolean | ((prev: boolean) => boolean)) => {
    setUseAgentModeLocal(prev => {
      const next = typeof val === 'function' ? val(prev) : val
      if (next && appMode === 'studio') {
        setAppMode('chat')
      }
      if (activeConversation?.id) {
        updateConversationMeta(activeConversation.id, { useAgentMode: next })
      }
      setNavUseAgentMode(next)
      return next
    })
  }, [activeConversation?.id, appMode, setAppMode, updateConversationMeta, setNavUseAgentMode])

  const [modeSwitching, setModeSwitching] = useState(false)
  const modeSwitchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [openFile, setOpenFile] = useState<AgentFile | null>(null)
  const [openFileAllFiles, setOpenFileAllFiles] = useState<AgentFile[]>([])  
  const bottomRef = useRef<HTMLDivElement>(null)
  const hydratedAgentRunsRef = useRef<Set<string>>(new Set())

  const closeSidePanel = useCallback(() => {
    setShowSidePanel(false)
    setSidePanelPreferredTab(undefined)
    setSidePanelContext(null)
  }, [])

  const openSidePanelForConversation = useCallback((preferredTab: ComputerPanelTab, conversationId?: string | null, runId?: string | null) => {
    if (!conversationId) return
    setSidePanelContext({ conversationId, runId: runId ?? null })
    setSidePanelPreferredTab(preferredTab)
    setShowSidePanel(true)
  }, [])

  useEffect(() => {
    if (!showSidePanel) return
    if (!activeConversation?.id || activeConversation.messages.length === 0) {
      closeSidePanel()
      return
    }
    if (sidePanelContext?.conversationId && sidePanelContext.conversationId !== activeConversation.id) {
      closeSidePanel()
    }
  }, [activeConversation?.id, activeConversation?.messages.length, closeSidePanel, showSidePanel, sidePanelContext?.conversationId])

  const handleOpenComputerPanel = useCallback(() => {
    const conversation = activeConversation
    if (!conversation?.id || conversation.messages.length === 0) return

    const latestAgentMessage = findLatestAgentRunMessage(conversation.messages)
    const activeRunBelongsToConversation = Boolean(
      agentState.runId
      && agentState.runtime.conversationId === conversation.id,
    )
    if (latestAgentMessage?.agentRunId && latestAgentMessage.agentRunId !== agentState.runId) {
      const previousUserMessage = conversation.messages
        .slice(0, conversation.messages.findIndex(item => item.id === latestAgentMessage.id))
        .reverse()
        .find(item => item.role === 'user')
      openSidePanelForConversation('summary', conversation.id, latestAgentMessage.agentRunId)
      void resumeAgentRun(
        latestAgentMessage.agentRunId,
        conversation.id,
        latestAgentMessage.id,
        previousUserMessage?.content ?? conversation.title,
      )
      return
    }

    const runId = latestAgentMessage?.agentRunId ?? (activeRunBelongsToConversation ? agentState.runId : null)
    if (!runId) return

    const preferred: ComputerPanelTab = getPreferredComputerMode(agentState)

    openSidePanelForConversation(preferred, conversation.id, runId)
  }, [
    activeConversation,
    agentState,
    openSidePanelForConversation,
    resumeAgentRun,
  ])

  // Blob fade state (brukes for Ã¥ fade ut ved melding)
  const [_blobFadingOut, setBlobFadingOut] = useState(false)

  // Hyperspeed state
  const [showHyperspeed, setShowHyperspeed] = useState(false)
  const [_hyperspeedDone, setHyperspeedDone] = useState(false)

  // Agent settings
  const [agentSettings, setAgentSettings] = useState<AgentSettings>({
    agentType: 'code',
    agentMode: 'safe',
    outputLanguage: 'auto',
    detailLevel: 'normal',
    documentType: 'report',
    autoSave: false,
  })

  // Sync agentMode from settings
  useEffect(() => {
    setAgentMode(agentSettings.agentMode)
  }, [agentSettings.agentMode])

  useEffect(() => {
    const scrollContainer = bottomRef.current?.closest('.chat-messages-area') as HTMLElement | null
    const distanceFromBottom = scrollContainer
      ? scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight
      : 0
    const agentIsRunning = isLiveAgentStatus(agentState.status)
    const shouldStickToBottom = !scrollContainer || distanceFromBottom < 240 || isStreaming || agentIsRunning
    if (!shouldStickToBottom) return

    const frame = window.requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [activeConversation?.messages, agentState.liveTasks.length, agentState.semanticEvents.length, agentState.status, isStreaming])

  // NÃ¥r bruker klikker agent i sidebar, aktiver agent-modus
  useEffect(() => {
    if (pendingAgentTask !== null) {
      setUseAgentMode(true)
      setPendingAgentTask(null)
    }
  }, [pendingAgentTask, setPendingAgentTask, setUseAgentMode])

  // Lytt pÃ¥ agent-complete event
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{
        convId: string
        msgId: string
        files: AgentFile[]
        suggestions: string[]
        sourcePack?: import('@/types').AgentSourceChip[]
        intentDecision?: import('@/types').AgentIntentDecision
        selectedCapabilities?: import('@/types').AgentSelectedCapability[]
        structuredOutput?: import('@/types').AgentStructuredOutput
        recurringProposal?: import('@/types').AgentRecurringProposal | null
        responseMode?: import('@/types').AgentIntentDecision['response_mode']
        memorySummary?: import('@/types').AgentMemorySummary
        workflowSummary?: import('@/types').AgentWorkflowSummary
        semanticEvents?: import('@/types').AgentSemanticEvent[]
        message: string
        status?: 'completed' | 'completed_with_warnings' | 'failed' | 'fatal_failed'
      }>
      const { convId, msgId, files, suggestions, sourcePack, intentDecision, selectedCapabilities, structuredOutput, recurringProposal, responseMode, memorySummary, workflowSummary, semanticEvents, message, status } = ev.detail
      updateAgentMessage(convId, msgId, {
        agentStatus: status ?? 'completed',
        agentFiles: files,
        agentSuggestions: suggestions,
        agentSourcePack: sourcePack,
        agentIntentDecision: intentDecision,
        agentSelectedCapabilities: selectedCapabilities,
        agentStructuredOutput: structuredOutput,
        agentMemorySummary: memorySummary,
        agentWorkflowSummary: workflowSummary,
        agentSemanticEvents: semanticEvents,
        agentRecurringProposal: recurringProposal ?? undefined,
        agentResponseMode: responseMode ?? 'default',
        content: message || '',
      })
    }
    window.addEventListener('agent-complete', handler)
    return () => window.removeEventListener('agent-complete', handler)
  }, [updateAgentMessage])

  // Register/unregister agent run for concurrency tracking
  useEffect(() => {
    const isRunning = isLiveAgentStatus(agentState.status)
    if (isRunning && agentState.runId) {
      registerAgentRun(agentState.runId)
    } else if (!isRunning && agentState.runId) {
      unregisterAgentRun(agentState.runId)
    }
    return () => {
      if (agentState.runId) unregisterAgentRun(agentState.runId)
    }
  }, [agentState.status, agentState.runId])

  useEffect(() => {
    const isRunning = isLiveAgentStatus(agentState.status)
    const hasRealComputerActivity = Boolean(agentState.browserFrame)
      || Boolean(agentState.browserSession)
      || agentState.terminalFrames.length > 0
      || agentState.files.length > 0
      || agentState.liveFiles.length > 0
      || Boolean(agentState.pendingApproval)
      || agentState.runtime.approvals.length > 0
    if (isRunning && hasRealComputerActivity) {
      const preferred: ComputerPanelTab = getPreferredComputerMode(agentState)
      openSidePanelForConversation(preferred, activeConversation?.id, agentState.runId)
    }
  }, [activeConversation?.id, agentState.status, agentState.runId, agentState.browserFrame, agentState.browserSession, agentState.terminalFrames.length, agentState.files.length, agentState.liveFiles.length, agentState.pendingApproval, agentState.runtime.approvals.length, agentState, openSidePanelForConversation])

  useEffect(() => {
    const conversation = activeConversation
    if (!conversation?.id) return

    let cancelled = false
    const messagesToHydrate = conversation.messages
      .map((message, index) => ({ message, index }))
      .filter(({ message }) => (
        (message.role === 'agent' || message.isAgentMessage)
        && message.agentRunId
        && !hydratedAgentRunsRef.current.has(`${message.id}:${message.agentRunId}`)
        && ((message.agentFiles?.length ?? 0) === 0 || message.agentStatus === 'running' || message.agentStatus === 'waiting_approval' || message.agentStatus === 'needs_takeover' || message.agentStatus === 'takeover_active')
        && message.agentRunId !== agentState.runId
      ))

    messagesToHydrate.slice(-3).forEach(({ message, index }) => {
      hydratedAgentRunsRef.current.add(`${message.id}:${message.agentRunId}`)
      fetchAgentRunState(message.agentRunId!)
        .then(snapshot => {
          if (cancelled || !snapshot) return
          const files = normalizeRunStateFiles(snapshot)
          const events = normalizeRunStateEvents(snapshot)
          const planningTasks = normalizeRunStatePlanningTasks(snapshot)
          const computer = normalizeRunStateComputerFrames(snapshot)
          const nextStatus = (snapshot.status as typeof message.agentStatus | undefined) ?? message.agentStatus
          updateAgentMessage(conversation.id, message.id, {
            agentFiles: files.length > 0 ? files : message.agentFiles,
            agentStatus: nextStatus,
            agentSuggestions: snapshot.suggestions ?? message.agentSuggestions,
            agentEvents: events.length > 0 ? events : message.agentEvents,
            agentPlanningTasks: planningTasks.length > 0 ? planningTasks : message.agentPlanningTasks,
            content: selectHydratedAgentContent(message.content, snapshot),
          })
          const shouldResumeRun = message.agentRunId !== agentState.runId
            && (nextStatus === 'running'
              || nextStatus === 'waiting_approval'
              || nextStatus === 'needs_takeover'
              || nextStatus === 'takeover_active'
              || Boolean(computer.browserFrame)
              || computer.terminalFrames.length > 0)
          if (shouldResumeRun) {
            const previousUserMessage = conversation.messages
              .slice(0, index)
              .reverse()
              .find(item => item.role === 'user')
            void resumeAgentRun(
              message.agentRunId!,
              conversation.id,
              message.id,
              previousUserMessage?.content ?? conversation.title,
            )
          }
          if (files.length > 0 || computer.browserFrame || computer.terminalFrames.length > 0) {
            openSidePanelForConversation(files.length > 0 ? 'editor' : computer.browserFrame ? 'browser' : 'terminal', conversation.id, message.agentRunId)
          }
        })
        .catch(() => {
          // Silent by design: hydration should never add visible noise to chat.
        })
    })

    return () => {
      cancelled = true
    }
  }, [activeConversation, agentState.runId, resumeAgentRun, updateAgentMessage, openSidePanelForConversation])

  useEffect(() => {
    const conversation = activeConversation
    if (!conversation?.id || isLiveAgentStatus(agentState.status)) return

    let cancelled = false
    fetchActiveAgentRun(conversation.id)
      .then(snapshot => {
        if (cancelled || !snapshot?.run_id || snapshot.run_id === agentState.runId) return
        const hydrateKey = `active:${conversation.id}:${snapshot.run_id}`
        if (hydratedAgentRunsRef.current.has(hydrateKey)) return
        const messageId = String((snapshot as { message_id?: string }).message_id ?? '')
        const message = conversation.messages.find(item => item.id === messageId)
          ?? conversation.messages.find(item => item.agentRunId === snapshot.run_id)
          ?? [...conversation.messages].reverse().find(item => item.role === 'agent' || item.isAgentMessage)
        if (!message) return
        hydratedAgentRunsRef.current.add(hydrateKey)

        const files = normalizeRunStateFiles(snapshot)
        const events = normalizeRunStateEvents(snapshot)
        const planningTasks = normalizeRunStatePlanningTasks(snapshot)
        const computer = normalizeRunStateComputerFrames(snapshot)
        updateConversationMeta(conversation.id, { useAgentMode: true })
        updateAgentMessage(conversation.id, message.id, {
          agentRunId: snapshot.run_id,
          agentFiles: files.length > 0 ? files : message.agentFiles,
          agentStatus: (snapshot.status as typeof message.agentStatus | undefined) ?? 'running',
          agentSuggestions: snapshot.suggestions ?? message.agentSuggestions,
          agentEvents: events.length > 0 ? events : message.agentEvents,
          agentPlanningTasks: planningTasks.length > 0 ? planningTasks : message.agentPlanningTasks,
          content: selectHydratedAgentContent(message.content, snapshot),
        })

        const previousUserMessage = conversation.messages
          .slice(0, conversation.messages.findIndex(item => item.id === message.id))
          .reverse()
          .find(item => item.role === 'user')
        void resumeAgentRun(
          snapshot.run_id,
          conversation.id,
          message.id,
          previousUserMessage?.content ?? conversation.title,
        )
        if (files.length > 0 || computer.browserFrame || computer.terminalFrames.length > 0 || events.some(event => ['tool_call', 'tool_result'].includes(event.type))) {
          openSidePanelForConversation(files.length > 0 ? 'editor' : computer.browserFrame ? 'browser' : computer.terminalFrames.length > 0 ? 'terminal' : 'summary', conversation.id, snapshot.run_id)
        }
      })
      .catch(() => {
        // Active-run lookup is best-effort; chat history should still render.
      })

    return () => {
      cancelled = true
    }
  }, [activeConversation, agentState.runId, agentState.status, resumeAgentRun, updateAgentMessage, updateConversationMeta, openSidePanelForConversation])

  const isAgentActive = isLiveAgentStatus(agentState.status)
  const runtimeSurface = getConversationRuntimeSurface({ appMode, studioOpen, useAgentMode })
  const latestAgentRunMessageForInput = activeConversation
    ? findLatestAgentRunMessage(activeConversation.messages)
    : undefined
  const agentStateBelongsToActiveConversation = Boolean(
    activeConversation?.id
    && agentState.runId
    && (
      agentState.runtime.conversationId === activeConversation.id
      || latestAgentRunMessageForInput?.agentRunId === agentState.runId
    ),
  )
  const visibleAgentState = agentStateBelongsToActiveConversation ? agentState : undefined
  const canOpenComputerPanel = Boolean(
    visibleAgentState?.runId
    || latestAgentRunMessageForInput?.agentRunId,
  )

  const runtime = useConversationRuntime({
    sendMessage,
    startAgent,
    stopStreaming,
    stopAgent,
    agentMode,
    defaultRouteOverride: runtimeSurface.routeOverride,
    fallbackTarget: runtimeSurface.fallbackTarget,
    runtimeSurface: runtimeSurface.surface,
    isAgentActive,
    appMode,
    canStartAgent,
    setAppMode,
    onNoCredits: () => setCreditsBannerState('empty'),
    onAgentLimit: () => setShowUpgradeCTA(true),
    onBeforeSend: (text) => {
      const convTitle = activeConversation?.title ?? 'Ny samtale'
      extractFromMessage(text, convTitle)
    },
    onStartChat: () => {
      setBlobFadingOut(true)
    },
    onStartAgent: () => {
      setBlobFadingOut(true)
      setShowHyperspeed(true)
      setHyperspeedDone(false)
    },
  })

  const handleSend = useCallback((
    text: string,
    mode?: AgentMode,
    attachments?: MessageAttachment[],
    backendMessage?: string,
  ) => {
    void runtime.sendConversationMessage(text, hasNoCredits, { mode, attachments, backendMessage })
  }, [runtime, hasNoCredits])

  const handleRegenerate = useCallback(() => {
    runtime.regenerateLast(hasNoCredits)
  }, [runtime, hasNoCredits])

  // Ã…pne fil-popup
  const handleOpenFile = async (file: AgentFile, allFiles?: AgentFile[]) => {
    let fileWithContent = file
    if (!file.content && file.path) {
      const runId = file.runId ?? agentState.runId ?? undefined
      const content = await fetchFileContent(file.path, runId)
      fileWithContent = { ...file, content: content || undefined }
    }
    const allWithContent = await Promise.all(
      (allFiles ?? []).map(async (f) => {
        if (f.content) return f
        if (!f.path) return f
        const runId = f.runId ?? agentState.runId ?? undefined
        const content = await fetchFileContent(f.path, runId)
        return { ...f, content: content || undefined }
      })
    )
    setOpenFile(fileWithContent)
    setOpenFileAllFiles(allWithContent.length > 0 ? allWithContent : [fileWithContent])
    closeSidePanel()
  }

  const handleDownloadFile = (file: AgentFile) => {
    if (file.downloadUrl) {
      const a = document.createElement('a')
      a.href = file.downloadUrl
      a.download = file.name
      a.click()
    } else if (file.content) {
      const blob = new Blob([file.content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      a.click()
      URL.revokeObjectURL(url)
    }
  }

  // Upgrade CTA component
  const maxAgents = plan === 'pro' ? 5 : 1
  const UpgradeCTA = showUpgradeCTA ? (
    <div style={{
      margin: '0 24px 12px',
      padding: '16px 20px',
      background: 'linear-gradient(135deg, rgba(26,147,254,0.08) 0%, rgba(139,92,246,0.08) 100%)',
      border: '1px solid rgba(26,147,254,0.25)',
      borderRadius: 12,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 16,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Rocket size={15} style={{ color: '#1A93FE', flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#E5E5E5' }}>
            {plan === 'free'
              ? 'Du kan bare kjøre én agentisk oppgave om gangen'
              : `Du har nådd grensen på ${maxAgents} samtidige agenter`
            }
          </span>
        </div>
        <p style={{ fontSize: 12, color: '#8A8A8A', margin: 0, lineHeight: 1.5 }}>
          {plan === 'free'
            ? 'Vent til den nåværende agenten er ferdig, eller oppgrader til Pro for å kjøre opptil 5 agenter samtidig.'
            : 'Vent til en av de aktive agentene er ferdig før du starter en ny.'}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        {plan === 'free' && (
          <button
            onClick={() => {
              setShowUpgradeCTA(false)
              openSettingsTab?.('billing')
            }}
            style={{
              padding: '7px 16px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #1A93FE 0%, #7C3AED 100%)',
              color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            Oppgrader til Pro
          </button>
        )}
        <button
          onClick={() => setShowUpgradeCTA(false)}
          style={{
            padding: '6px 12px', borderRadius: 8, border: '1px solid #3A3A3A',
            background: 'transparent', color: '#8A8A8A', fontSize: 12, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Lukk
        </button>
      </div>
    </div>
  ) : null

  // Credits banner â€” matches Manus design: dark bg, border-radius, sparkle icon, white Continue button
  const CreditsBanner = creditsBannerState ? (
    <div style={{
      margin: '0 24px 12px',
      padding: '14px 16px',
      background: '#1C1C1C',
      border: '1px solid #2A2A2A',
      borderRadius: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
        {/* Sparkle icon */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="#E5E5E5" opacity="0.85"/>
        </svg>
        <span style={{ fontSize: 13, color: '#C5C5C5', lineHeight: 1.5 }}>
          {creditsBannerState === 'empty'
            ? 'Du har ikke nok kreditter. Fyll på kontoen din for å fortsette.'
            : 'Kredittene dine er oppdatert. Du kan fortsette samtalen.'}
        </span>
      </div>
      <button
        onClick={() => {
          if (creditsBannerState === 'refilled') {
            setCreditsBannerState(null)
          } else {
            // Open billing tab to upgrade
            openSettingsTab?.('billing')
          }
        }}
        style={{
          padding: '7px 18px',
          borderRadius: 8,
          border: '1px solid #4A4A4A',
          background: creditsBannerState === 'refilled' ? '#FFFFFF' : 'transparent',
          color: creditsBannerState === 'refilled' ? '#111111' : '#E5E5E5',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {creditsBannerState === 'refilled' ? 'Fortsett' : 'Oppgrader'}
      </button>
    </div>
  ) : null

  const hasMessages = activeConversation && activeConversation.messages.length > 0
  const activeConversationRunIds = new Set(
    (activeConversation?.messages ?? [])
      .map(message => message.agentRunId)
      .filter((runId): runId is string => Boolean(runId)),
  )
  const sidePanelRunMatchesCurrentAgentState = Boolean(
    !sidePanelContext?.runId || sidePanelContext.runId === agentState.runId,
  )
  const sidePanelRunCanHydrateFromActiveConversation = Boolean(
    sidePanelContext?.runId
    && activeConversationRunIds.has(sidePanelContext.runId)
    && (!agentState.runId || agentState.runtime.conversationId !== activeConversation?.id),
  )
  const canShowAgentSidePanel = Boolean(
    showSidePanel
    && hasMessages
    && activeConversation?.id
    && sidePanelContext?.conversationId === activeConversation.id
    && (sidePanelRunMatchesCurrentAgentState || sidePanelRunCanHydrateFromActiveConversation),
  )

  // When agent is active, stop button should stop the agent; otherwise stop chat streaming
  const handleStop = useCallback(() => {
    if (isAgentActive) {
      stopAgent()
    } else {
      stopStreaming()
    }
  }, [isAgentActive, stopAgent, stopStreaming])

  // isStreaming for the input: true when chat is streaming OR agent is running
  const isInputStreaming = isStreaming || isAgentActive

  // Per-chat enabled connectors
  const enabledConnectors = activeConversation?.enabledConnectors ?? null
  const handleToggleConnector = useCallback((connectorId: string, enabled: boolean) => {
    if (!activeConversation?.id) return
    const current = activeConversation.enabledConnectors ?? []
    const updated = enabled
      ? [...current.filter(id => id !== connectorId), connectorId]
      : current.filter(id => id !== connectorId)
    updateConversationMeta(activeConversation.id, { enabledConnectors: updated })
  }, [activeConversation?.id, activeConversation?.enabledConnectors, updateConversationMeta])

  const chatInputProps = {
    onSend: handleSend,
    onStop: handleStop,
    isStreaming: isInputStreaming,
    language: settings.language,
    agentMode,
    onAgentModeChange: (m: AgentMode) => setAgentSettings(s => ({ ...s, agentMode: m })),
    isAgentActive,
    useAgentMode,
    runtimeSurface: runtimeSurface.surface,
    showAgentControls: runtimeSurface.showAgentControls,
    onToggleAgentMode: () => {
      // Fade-up animasjon ved bytte mellom chat og agent
      setModeSwitching(true)
      if (modeSwitchTimeout.current) clearTimeout(modeSwitchTimeout.current)
      modeSwitchTimeout.current = setTimeout(() => {
        setUseAgentMode(v => !v)
        setModeSwitching(false)
      }, 180)
    },
    agentState: visibleAgentState,
    onOpenTerminal: canOpenComputerPanel ? handleOpenComputerPanel : undefined,
    agentSettings,
    onAgentSettingsChange: setAgentSettings,
    memory,
    onAddMemory: (key: string, value: string) => addMemory(key, value, activeConversation?.title ?? 'Manuelt'),
    onRemoveMemory: removeMemory,
    onClearMemory: clearMemory,
    hasMessages: !!hasMessages,
    enabledConnectors,
    onToggleConnector: handleToggleConnector,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    suggestionOverride: runtimeSurface.isStudioSplit ? (STUDIO_SUGGESTIONS as any) : undefined,
  }

  // â”€â”€ Velkomstskjerm â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (!hasMessages) {
    const isStudioSplit = runtimeSurface.isStudioSplit
    const welcomeCopy = runtimeSurface.showAgentControls ? rotatingAgentWelcome : rotatingChatWelcome
    const welcomeTitle = isStudioSplit ? 'Hva vil du bygge?' : welcomeCopy.title
    const welcomeSubtitle = isStudioSplit
      ? 'Beskriv en app, trigger, grensesnitt eller automatisering du vil lage'
      : welcomeCopy.subtitle
    const welcomeAnimationKey = isStudioSplit
      ? `studio-${settings.language}-${runtimeSurface.surface}`
      : welcomeCopy.rotationKey
    return (
      <div style={{
        display: 'flex', flex: 1, overflow: 'hidden', position: 'relative',
        flexDirection: 'row',
        opacity: modeSwitching ? 0 : 1,
        transform: modeSwitching ? 'translateY(12px)' : 'translateY(0)',
        transition: modeSwitching ? 'none' : 'opacity 0.28s ease, transform 0.28s ease',
      }}>
        {/* Left: chat area â€” capped at 750px in Studio mode */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minWidth: 0, maxWidth: isStudioSplit ? 750 : undefined }}>
          {/* Upgrade CTA on welcome screen */}
          {UpgradeCTA && <div style={{ padding: '12px 24px 0' }}>{UpgradeCTA}</div>}
          {/* Credits banner on welcome screen */}
          {CreditsBanner && <div style={{ padding: '12px 24px 0' }}>{CreditsBanner}</div>}
          <WelcomeLayout
            title={welcomeTitle}
            subtitle={welcomeSubtitle}
            animatedPlaceholderText={isStudioSplit ? 'Beskriv hva du vil bygge...' : welcomeCopy.input}
            animationKey={welcomeAnimationKey}
            isAgent={runtimeSurface.showAgentControls}
            isStudio={isStudioSplit}
            chatInputProps={chatInputProps}
            blobFadingOut={_blobFadingOut}
            showHyperspeed={showHyperspeed}
            onHyperspeedComplete={() => setHyperspeedDone(true)}
          />
        </div>
        {/* Right: side panel */}
        {isStudioSplit && (
          appMode === 'studio' ? (
            <StudioPanel
              agentState={agentState}
              memory={memory}
              onAddMemory={(key, value) => addMemory(key, value, activeConversation?.title ?? 'Manuelt')}
              onRemoveMemory={removeMemory}
              onClose={() => setStudioOpen(false)}
            />
          ) : canShowAgentSidePanel ? (
            <AgentSidePanel
              state={agentState}
              preferredTab={sidePanelPreferredTab}
              onBrowserTakeover={() => requestBrowserTakeover(sidePanelContext?.runId ?? undefined)}
              onBrowserResume={() => resumeBrowserFromTakeover(sidePanelContext?.runId ?? undefined)}
              onClose={closeSidePanel}
              onFetchFile={fetchFileContent}
            />
          ) : null
        )}
        {openFile && (
          <FilePopup
            file={openFile}
            allFiles={openFileAllFiles}
            onClose={() => setOpenFile(null)}
            onDownload={handleDownloadFile}
          />
        )}
      </div>
    )
  }

  // â”€â”€ Chat med meldinger â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const isStudioSplitChat = runtimeSurface.isStudioSplit
  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <div className="chat-view" style={{ flex: 1, maxWidth: isStudioSplitChat ? 750 : undefined }}>
        <div className="chat-messages-area">
          <div className="chat-messages-inner">
            {activeConversation.messages.map((msg, i) => {
              const isLast = i === activeConversation.messages.length - 1

              if (msg.isAgentMessage || msg.role === 'agent') {
                const isActiveAgentMsg = msg.id === agentState.agentMessageId
                const tasks = isActiveAgentMsg ? agentState.liveTasks : (msg.agentTasks ?? [])
                const files = isActiveAgentMsg ? agentState.liveFiles : (msg.agentFiles ?? [])
                const status = isActiveAgentMsg
                  ? (agentState.status === 'completed' ? 'completed'
                    : agentState.status === 'completed_with_warnings' ? 'completed_with_warnings'
                    : agentState.status === 'failed' ? 'failed'
                    : agentState.status === 'fatal_failed' ? 'fatal_failed'
                    : agentState.status === 'stopped' ? 'stopped'
                    : agentState.status === 'interrupted' ? 'interrupted'
                    : agentState.status === 'resume_available' ? 'resume_available'
                    : agentState.status === 'stale' ? 'stale'
                    : 'running')
                  : (msg.agentStatus ?? 'completed')

                const agentSuggestions = (status === 'completed' || status === 'completed_with_warnings')
                  ? (msg.agentSuggestions && msg.agentSuggestions.length > 0
                    ? msg.agentSuggestions
                    : (isActiveAgentMsg && agentState.suggestions ? agentState.suggestions : []))
                  : []

                const planningTasks = isActiveAgentMsg ? agentState.planningTasks : (msg.agentPlanningTasks ?? undefined)
                const semanticEvents = isActiveAgentMsg ? agentState.semanticEvents : (msg.agentSemanticEvents ?? [])
                const subagentRuntime = isActiveAgentMsg ? agentState.subagentRuntime : (msg.agentSubagentRuntime ?? [])
                return (
                  <AgentNarrativeMessage
                    key={msg.id}
                    message={{
                      ...msg,
                      agentTasks: tasks,
                      agentFiles: files,
                      agentStatus: status,
                      agentSuggestions: agentSuggestions,
                      agentSemanticEvents: semanticEvents,
                      agentSubagentRuntime: subagentRuntime,
                    }}
                    planningTasks={planningTasks}
                    onOpenFile={handleOpenFile}
                    onSuggestion={(text) => startAgent(text, agentMode, false)}
                  />
                )
              }

              // Sjekk om noen bruker-melding i samtalen inneholder /skill-creator
              const hasSkillCreator = activeConversation.messages.some(
                m => m.role === 'user' && m.content.toLowerCase().includes('/skill-creator')
              )

              return (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  isLast={isLast}
                  onRegenerate={isLast ? handleRegenerate : undefined}
                  conversationHasSkillCreator={hasSkillCreator}
                  onAskUserSubmit={msg.askUserInput && !msg.askUserInput.answered
                    ? (answers) => handleAskUserSubmit(answers, msg.askUserInput!.questions, appMode === 'studio')
                    : undefined
                  }
                />
              )
            })}
            <div ref={bottomRef} style={{ height: 16 }} />
          </div>
        </div>

        {/* Upgrade CTA â€” vises nÃ¥r agentgrensen er nÃ¥dd */}
        {UpgradeCTA}
        {/* Credits banner */}
        {CreditsBanner}

        {/* Godkjenning-banner */}
        {agentState.pendingApproval && (
          <div style={{
            margin: '0 24px 12px',
            padding: '12px 16px',
            background: 'rgba(245,158,11,0.1)',
            border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#F59E0B', marginBottom: 2 }}>
                Godkjenning kreves
              </div>
              <div style={{ fontSize: 12, color: '#9A9A9A' }}>
                {agentState.pendingApproval.description}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={() => approveAction(false)}
                style={{
                  padding: '6px 12px', borderRadius: 7, border: '1px solid #3A3A3A',
                  background: 'transparent', color: '#9A9A9A', fontSize: 12, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Avslå
              </button>
              <button
                onClick={() => approveAction(true)}
                style={{
                  padding: '6px 12px', borderRadius: 7, border: 'none',
                  background: '#F59E0B', color: '#000', fontSize: 12, cursor: 'pointer',
                  fontWeight: 600, fontFamily: 'inherit',
                }}
              >
                Godkjenn
              </button>
            </div>
          </div>
        )}

        {agentState.unavailableConnectors && agentState.unavailableConnectors.length > 0 && (
          <div style={{
            margin: '0 24px 12px',
            padding: '11px 14px',
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.24)',
            borderRadius: 10,
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#F59E0B', marginBottom: 4 }}>
              Noen valgte connectors er ikke tilgjengelige i denne kjøringen
            </div>
            <div style={{ fontSize: 12, color: '#9A9A9A', lineHeight: 1.5 }}>
              {agentState.unavailableConnectors.join(', ')}
            </div>
          </div>
        )}

        <ChatInput {...chatInputProps} />
      </div>

      {(canShowAgentSidePanel || studioOpen) && (
        appMode === 'studio' ? (
          <StudioPanel
            agentState={agentState}
            memory={memory}
            onAddMemory={(key, value) => addMemory(key, value, activeConversation?.title ?? 'Manuelt')}
            onRemoveMemory={removeMemory}
            onClose={() => setStudioOpen(false)}
          />
        ) : (
          <AgentSidePanel
            state={agentState}
            preferredTab={sidePanelPreferredTab}
            onBrowserTakeover={() => requestBrowserTakeover(sidePanelContext?.runId ?? undefined)}
            onBrowserResume={() => resumeBrowserFromTakeover(sidePanelContext?.runId ?? undefined)}
            onClose={closeSidePanel}
            onFetchFile={fetchFileContent}
          />
        )
      )}
      {openFile && (
        <FilePopup
          file={openFile}
          allFiles={openFileAllFiles}
          onClose={() => setOpenFile(null)}
          onDownload={handleDownloadFile}
        />
      )}
    </div>
  )
}
